// AI generation methods for TheoryCoach
// Extracted to reduce main file size and prevent Cursor crashes

import { logger } from '../utils/logger';
import { TheoryCoachContext } from './theoryCoach.types';
import { SYSTEM_PROMPT } from './theoryCoach.prompts';
import { formatResponse } from './theoryCoach.formatting';
import {
  getCorrectAnswerTemplate,
  getIncorrectAnswerTemplate,
  summarizeExplanation
} from './theoryCoach.templates';
import { getLanguageName, getFallbackResponse } from './theoryCoach.core';
import { callOpenAI } from './theoryCoach.openai';
import { generateFollowUpSuggestions } from './theoryCoach.ai.followUp';

export { callOpenAI, generateFollowUpSuggestions };

/**
 * Build question prompt for AI
 */
export const buildQuestionPrompt = (context: TheoryCoachContext): string => {
  const languageName = getLanguageName(context.language);
  const optionsText = context.currentQuestion?.options
    ? context.currentQuestion.options.map((opt, _idx) => `${opt.letter}. ${opt.text}`).join('\n')
    : '';
  const questionContext = context.currentQuestion
    ? `\n\nCURRENT QUESTION:
Question: ${context.currentQuestion.text}
${optionsText ? `Answer Options:\n${optionsText}` : ''}
Correct Answer: ${context.currentQuestion.correctAnswer}
Explanation: ${context.currentQuestion.explanation}
Topic: ${context.currentQuestion.topic}`
    : '';

  // Add conversation history to prevent repetition
  let conversationContext = '';
  if (context.conversationHistory && context.conversationHistory.length > 0) {
    const recentHistory = context.conversationHistory.slice(-6); // Last 6 messages (3 user + 3 coach) - increased to catch more repetition
    conversationContext = `\n\nPREVIOUS CONVERSATION (DO NOT REPEAT THESE RESPONSES):
${recentHistory.map(msg => `${msg.role === 'user' ? 'USER' : 'COACH'}: ${typeof msg.content === 'string' ? msg.content : String(msg.content)}`).join('\n')}

CRITICAL ANTI-REPETITION RULES:
- Your response must be COMPLETELY DIFFERENT from ALL previous coach responses above
- Do NOT repeat the same rule explanation, even with different wording
- Do NOT repeat the same key concepts or phrases
- If a previous response already explained the rule, provide NEW information or a different angle
- If the explanation above already covers the key rule, focus on a DIFFERENT aspect or provide an example
- NEVER say "Rule: [rule]" if that rule was already mentioned in previous responses`;
  }

  // Detect card clicks and provide contextual coaching
  const userMessage = (context.userMessage || '').toLowerCase().trim();
  const isGuessed = /^(i guessed|ik gokte|خمّنت|i guess|ik raad|خمنت)/i.test(userMessage);
  const isMisread = /(misread|misread the|misread a|misread the light|misread the sign|misread the traffic|verkeerd gelezen|leesfout|قرأت بشكل خاطئ|قرأت خطأ)/i.test(userMessage);
  const isExplainFurther = userMessage.includes('explain further') || 
                           userMessage.includes('explain more') ||
                           (context.language === 'nl' && (userMessage.includes('leg uit') || userMessage.includes('meer uitleg') || userMessage.includes('leg verder'))) ||
                           (context.language === 'ar' && (userMessage.includes('اشرح أكثر') || userMessage.includes('مزيد من الشرح')));
  const isConfused = /(confused|mixed up|verward|verwarring|مشوش|مربك)/i.test(userMessage);
  
  let coachingGuidance = '';
  if (isGuessed) {
    coachingGuidance = `\n\nUSER SAID "I GUESSED":
- This indicates a knowledge gap or uncertainty
- PROVIDE THE SPECIFIC RULE EXPLANATION from this question's explanation above
- Extract the KEY RULE - provide a VERY BRIEF, CLEAR, PASS-READY explanation (1 sentence max, ≤15 words)
- Use the first sentence of the explanation or the most important rule statement
- Example: "Amber means stop unless you've already crossed the line."
- Be direct and specific - state the rule clearly and concisely
- DO NOT ask questions - provide the rule directly
- Keep it to 1 sentence max, ≤15 words - just the essential rule the user must know to pass
- Use the EXACT wording from the explanation when possible
- NEVER repeat previous responses
- BE EXTREMELY BRIEF - users want quick answers`;
  } else if (isExplainFurther) {
    coachingGuidance = `\n\nUSER SAID "EXPLAIN FURTHER":
- User wants the key rule explanation - they need to understand the CORE RULE
- CRITICAL: Extract ONLY the essential rule from the explanation above - the rule that determines the answer
- Look for the rule statement in the explanation (usually the first sentence or the sentence that states the rule)
- PROVIDE ONLY THE RULE - do not explain why, do not give examples, do not ask questions
- Format: State the rule directly as a fact (e.g., "Amber means stop unless you've already crossed the line.")
- DO NOT say "The rule is..." or "According to..." - just state the rule directly
- DO NOT mention which answer is correct - only state the rule
- DO NOT ask questions like "Does that make sense?" - just provide the rule
- Keep it to 1 sentence max (under 20 words if possible)
- Use the EXACT wording from the explanation when possible
- NEVER repeat previous responses`;
  } else if (isMisread) {
    coachingGuidance = `\n\nUSER SAID "I MISREAD THE QUESTION" or "I MISREAD":
- This indicates they need to read more carefully
- RESPOND WITH: "Take your time, read carefully." (keep it simple and encouraging)
- This is a simple encouragement message - no need for detailed explanation
- Keep it to 1 sentence max
- NEVER repeat previous responses
- DO NOT ask questions - just provide the encouragement`;
  } else if (isConfused) {
    coachingGuidance = `\n\nUSER SAID "I'M CONFUSED":
- This indicates they mixed up rules or concepts
- PROVIDE A DIRECT ANSWER clarifying the SPECIFIC rule that applies here
- Reference the actual question content and explanation
- Give specific guidance: "This question is about [specific rule from question]. The key point is [distinction from explanation]."
- Keep it to 1 sentence max, ≤15 words (prefer brevity)
- NEVER repeat previous responses
- BE EXTREMELY BRIEF - users want quick answers`;
  }

  // STRICT: Add misconception context if available
  const misconceptionContext = (context as any).misconceptionContext;
  let misconceptionWarning = '';
  if (misconceptionContext?.lockedMisconceptionId) {
    misconceptionWarning = `\n\n⚠️ CRITICAL: MISCONCEPTION LOCKED (${misconceptionContext.lockedMisconceptionId})
- You MUST address ONLY this locked misconception
- Round: ${misconceptionContext.roundNumber || 1}
- Your response must:
  - Address the specific reasoning path of this misconception
  - Stay within the same logical chain
  - NOT introduce new misconceptions or unrelated topics
  - Match the round's purpose (Round 1: explain why reasoning is wrong, Round 2: clarify misconception, Round 3: reinforce correct rule)
`;
  }

  // For "I guessed", emphasize using the explanation directly
  const explanationEmphasis = isGuessed && context.currentQuestion?.explanation
    ? `\n\nIMPORTANT: The user said "I guessed" - they need the specific rule. Use the explanation above to provide the key rule. Extract the main rule statement (e.g., "Amber means stop unless you've already crossed the line").`
    : '';

  return `${SYSTEM_PROMPT}

${misconceptionWarning}${questionContext}${conversationContext}${explanationEmphasis}

USER SELECTED CARD: "${context.userMessage}"
${coachingGuidance}

⚠️ QUESTION-SCOPED RESPONSE — STRICT RULE ⚠️
Your response MUST be scoped to THIS SPECIFIC QUESTION (Question ID: ${context.currentQuestion?.text ? 'current' : 'N/A'}).
- Response MUST explicitly relate to THIS question's context: "${context.currentQuestion?.text || 'N/A'}"
- Response MUST reference THIS question's scenario, topic, or elements
- Response MUST NOT sound interchangeable with another question
- If you cannot provide a question-specific response, keep it very brief and rule-focused

COACH RESPONSE ALIGNMENT — MANDATORY:
- The user selected a follow-up card - this card represents a SINGLE intent
- You MUST treat the selected card as a micro-prompt
- Your response MUST address ONLY that card's intent
- Do NOT introduce new reasoning, examples, or concepts
- Your response must DIRECTLY reflect the card wording or meaning
- Be 1–2 short sentences (preferably 1 sentence, ≤15 words)
- Prepare the user for the next phase

PHASE ALIGNMENT (based on phase):
${misconceptionWarning ? `- Phase ${misconceptionContext?.roundNumber || 1}: ` : '- Phase 1: '}Acknowledge reasoning + highlight flaw
${misconceptionWarning ? `- Phase ${misconceptionContext?.roundNumber || 2}: ` : '- Phase 2: '}Respond BRIEFLY (1 sentence) - take-home rule is added separately by the system

YOU ARE NOT ALLOWED TO:
- Generalize beyond the card
- Re-explain the full rule early
- Introduce new mistakes or alternative reasoning
- Ask questions back
- Provide generic responses

ANSWER THEIR CARD INTENT:
- Answer directly and clearly - PROVIDE ANSWERS, NOT QUESTIONS
- CRITICAL: Be EXTREMELY BRIEF - 1 sentence preferred, ≤15 words if possible
- Match the EXACT intent of the selected card
- If card says "I thought amber always means stop": Address ONLY that misconception about amber
- If card says "When must I stop at amber?": Answer ONLY when to stop at amber
- If card says "What does amber mean exactly?": Explain ONLY what amber means
- Use simple language
- Keep it VERY SHORT - 1 sentence max for all responses (only use 2 sentences if absolutely necessary and keep total ≤20 words)
- Reference specific road signs, rules, or answer options if relevant
- Address the SPECIFIC intent of the card they selected
- Use the question context above to give an accurate answer
- NEVER repeat the same response - always provide NEW, SPECIFIC guidance
- NEVER ask questions back - provide direct answers and guidance

CRITICAL: Your response must be DIFFERENT from any previous responses in this conversation.
NEVER say generic phrases like "Great progress! Let's keep going" - always be specific.
NEVER ask questions - provide direct answers and guidance.
BE EXTREMELY BRIEF - users want quick, concise answers, not long explanations.

Respond in ${languageName} - be natural, helpful, and exam-focused.

Your response:`;
};

/**
 * Build check-in prompt for OpenAI (SHORT messages)
 */
export const buildCheckInPrompt = (
  focusArea: any | null,
  recentMistakes: number,
  mistakesInTopic: number,
  currentTopic: string,
  language: string
): string => {
  const languageName = getLanguageName(language);
  
  if (focusArea) {
    const areaName = focusArea.testName || focusArea.testId || 'this area';
    return `You are Theory Coach - a calm, exam-focused driving theory tutor.

USER IS STRUGGLING:
- Focus area: ${areaName}
- Mistakes in this area: ${mistakesInTopic}
- Current topic: ${currentTopic}

GENERATE A SHORT CHECK-IN (1-2 sentences max):
- Acknowledge the challenge briefly
- Provide quick guidance (slow down, read carefully)
- Keep it SHORT and CALM
- No explanations, just encouragement

REQUIREMENTS:
- Maximum 2 sentences
- Keep it SHORT (under 15 words total)
- Be CALM and SUPPORTIVE
- Never use the word "weak"
- Use simple language
- Respond in ${languageName}

Example: "I notice Priority Rules are challenging. Slow down and read carefully."

Your response:`;
  } else {
    return `You are Theory Coach - a calm, exam-focused driving theory tutor.

USER IS STRUGGLING:
- Recent mistakes: ${recentMistakes}
- Current topic: ${currentTopic}

GENERATE A SHORT CHECK-IN (1 sentence):
- Quick encouragement
- Simple guidance (take time, read carefully)
- Keep it SHORT and CALM

REQUIREMENTS:
- Maximum 1 sentence
- Keep it SHORT (under 10 words)
- Be CALM and SUPPORTIVE
- Never use the word "weak"
- Use simple language
- Respond in ${languageName}

Example: "Take your time and read carefully."

Your response:`;
  }
};

/**
 * Build proactive feedback prompt for OpenAI
 */
export const buildProactiveFeedbackPrompt = (context: TheoryCoachContext, explanation: string): string => {
  const languageName = getLanguageName(context.language);
  const isCorrect = context.isCorrect ? 'correct' : 'incorrect';
  
  return `You are Theory Coach - a friendly, structured, and encouraging driving theory tutor.

USER JUST ANSWERED A QUESTION:
- Answer was: ${isCorrect}
- Brief explanation: ${explanation}
- Question topic: ${context.currentQuestion?.topic || 'General driving theory'}
- Question ${context.questionNumber || 1} of ${context.totalQuestions || 10}

GENERATE PROACTIVE FEEDBACK:
${context.isCorrect 
  ? '- Congratulate them briefly (1 sentence)\n- Provide the explanation naturally\n- Encourage them to continue (1 sentence)'
  : '- Acknowledge the mistake positively (1 sentence)\n- Provide the explanation clearly\n- Encourage them to try another question (1 sentence)'
}

REQUIREMENTS:
- Keep it SHORT (1-2 sentences max)
- Be ENCOURAGING and POSITIVE
- Never use the word "weak"
- Never guarantee passing
- Use simple language
- Respond in ${languageName}

Your response:`;
};

/**
 * Get answer feedback (after correct/incorrect) - PROACTIVE
 * Uses OpenAI for better quality if available, otherwise uses templates
 */
export const getAnswerFeedback = async (
  openai: any,
  useOpenAI: boolean,
  context: TheoryCoachContext
): Promise<string> => {
  if (!context.currentQuestion) {
    return getFallbackResponse(context);
  }

  const explanation = context.currentQuestion.explanation || '';
  const briefExplanation = summarizeExplanation(explanation, context.language);

  // Use OpenAI for proactive feedback if available (better quality)
  if (useOpenAI && openai) {
    try {
      const prompt = context.customPrompt ?? buildProactiveFeedbackPrompt(context, briefExplanation);
      const response = await Promise.race([
        callOpenAI(openai, prompt, context.language),
        new Promise<string>((_, reject) => 
          setTimeout(() => reject(new Error('OpenAI timeout')), 5000)
        )
      ]);
      return formatResponse(response, context.language);
    } catch (error: any) {
      logger.debug('OpenAI feedback failed, using template:', error.message);
      // Fall through to template fallback
    }
  }

  // Fallback to templates (faster, no API cost)
  if (context.isCorrect) {
    return getCorrectAnswerTemplate(context.language, briefExplanation);
  } else {
    return getIncorrectAnswerTemplate(context.language, briefExplanation);
  }
};

/**
 * Answer user's question - Use OpenAI only (no Hugging Face fallback)
 */
export const answerUserQuestion = async (
  openai: any,
  useOpenAI: boolean,
  context: TheoryCoachContext
): Promise<string> => {
  if (!context.userMessage) {
    return getFallbackResponse(context);
  }

  const input = context.userMessage.trim();
  const wordCount = input.split(/\s+/).filter(Boolean).length;
  const language = context.language || 'en';
  
  // Check for "explain further" patterns first - these should always get the rule, not clarification
  const isExplainFurther = input.toLowerCase().includes('explain further') || 
                           input.toLowerCase().includes('explain more') ||
                           (language === 'nl' && (input.toLowerCase().includes('leg uit') || input.toLowerCase().includes('meer uitleg') || input.toLowerCase().includes('leg verder'))) ||
                           (language === 'ar' && (input.toLowerCase().includes('اشرح أكثر') || input.toLowerCase().includes('مزيد من الشرح')));
  
  // If it's "explain further", skip all checks and go straight to rule extraction
  if (isExplainFurther) {
    // Don't use clarify prompt - go straight to AI or fallback which will extract the rule
    // Continue to prompt building below
  } else {
  const isOutOfScope =
    /(score|pass|passing|app|dashboard|next test|other question|previous question|future question)/i.test(input);
  if (isOutOfScope) {
    return language === 'nl'
      ? 'Dit valt buiten deze vraag - focus op de regel hier.'
      : language === 'ar'
      ? 'هذا خارج هذا السؤال - ركّز على القاعدة هنا.'
      : 'This is outside this question - focus on the rule used here.';
  }
  if (!input || wordCount < 3) {
    const { getClarifyPrompt } = require('./theoryCoach.prompts');
    return getClarifyPrompt(language);
    }
  }
  if (!context.currentQuestion) {
    return language === 'nl'
      ? 'Dit valt buiten deze vraag - focus op de regel hier.'
      : language === 'ar'
      ? 'هذا خارج هذا السؤال - ركّز على القاعدة هنا.'
      : 'This is outside this question - focus on the rule used here.';
  }

  // Build prompt with current question context only
  const prompt = buildQuestionPrompt(context);
  
  // Use OpenAI only (required)
  if (!useOpenAI || !openai) {
    logger.warn('OpenAI not configured. Cannot answer user question.');
    return getFallbackResponse(context);
  }
  
  try {
    const response = await Promise.race([
      callOpenAI(openai, prompt, context.language),
      new Promise<string>((_, reject) => 
        setTimeout(() => reject(new Error('OpenAI timeout')), 6000)
      )
    ]);
    const formatted = formatResponse(response, context.language);
    
    // Special handling for "Explain further" - must be a direct rule statement
    const userMessage = (context.userMessage || '').toLowerCase().trim();
    const isExplainFurther = userMessage.includes('explain further') || 
                             userMessage.includes('explain more') ||
                             (context.language === 'nl' && (userMessage.includes('leg uit') || userMessage.includes('meer uitleg') || userMessage.includes('leg verder'))) ||
                             (context.language === 'ar' && (userMessage.includes('اشرح أكثر') || userMessage.includes('مزيد من الشرح')));
    
    if (isExplainFurther) {
      // For "explain further", validate that it's a direct rule statement
      // CRITICAL: Must provide the rule, not ask questions or be generic
      const formattedLower = formatted.toLowerCase();
      
      // Check for question patterns (more aggressive)
      const isQuestion = formatted.trim().endsWith('?') || 
                         formattedLower.includes('does that') ||
                         formattedLower.includes('make sense') ||
                         formattedLower.includes('understand') ||
                         formattedLower.includes('can you tell') ||
                         formattedLower.includes('can you clarify') ||
                         formattedLower.includes('what part') ||
                         formattedLower.includes('which part') ||
                         formattedLower.includes('i\'m not sure') ||
                         formattedLower.includes('not sure what') ||
                         formattedLower.includes('tell me which') ||
                         formattedLower.includes('helpt dat') ||
                         formattedLower.includes('begrijp je') ||
                         formattedLower.includes('wat bedoel je') ||
                         formattedLower.includes('هل تفهم') ||
                         formattedLower.includes('هل هذا') ||
                         formattedLower.includes('ما الذي تقصده');
      
      // Check for generic/unhelpful patterns
      const isGeneric = formattedLower.includes('i\'m not sure') ||
                       formattedLower.includes('not sure what you\'re asking') ||
                       formattedLower.includes('can you tell me which part') ||
                       formattedLower.includes('which part of the question') ||
                       formattedLower.includes('what are you unsure about');
      
      // Check if it looks like a rule statement
      const looksLikeRule = formattedLower.includes('means') || 
                           formattedLower.includes('must') ||
                           formattedLower.includes('should') ||
                           formattedLower.includes('required') ||
                           formattedLower.includes('rule') ||
                           formattedLower.includes('betekent') ||
                           formattedLower.includes('moet') ||
                           formattedLower.includes('regel') ||
                           formattedLower.includes('يجب') ||
                           formattedLower.includes('يعني') ||
                           formattedLower.includes('قاعدة') ||
                           (formatted.length < 120 && !isQuestion); // Short non-question responses are likely rules
      
      // If it's asking a question, is generic, or doesn't look like a rule, use fallback
      if (isQuestion || isGeneric || !looksLikeRule) {
        logger.warn('Explain further response rejected - not a rule statement:', formatted);
        throw new Error('Explain further response is not a direct rule statement');
      }
    }
    
    // General validation for all responses
    const formattedLower = formatted.toLowerCase();
    const isQuestion = formatted.trim().endsWith('?');
    const isGeneric = formattedLower.includes('great progress') || 
                      formattedLower.includes('keep going') ||
                      formattedLower.includes('which detail') ||
                      formattedLower.includes('what detail') ||
                      formattedLower.includes('i\'m not sure') ||
                      formattedLower.includes('not sure what you\'re asking') ||
                      formattedLower.includes('can you tell me which part') ||
                      formattedLower.includes('welk detail') ||
                      formattedLower.includes('wat bedoel je') ||
                      formattedLower.includes('ما التفصيل') ||
                      formattedLower.includes('ما الذي تقصده');
    
    // Check for repetition with previous coach messages
    const isRepetitive = context.conversationHistory && context.conversationHistory.length > 0
      ? context.conversationHistory
          .filter(msg => msg.role === 'coach')
          .some(msg => {
            const prevContent = (typeof msg.content === 'string' ? msg.content : String(msg.content)).toLowerCase();
            const currentContent = formatted.toLowerCase();
            // Check if more than 50% of words are the same (simple similarity check)
            const prevWords = prevContent.split(/\s+/).filter(w => w.length > 3);
            const currentWords = currentContent.split(/\s+/).filter(w => w.length > 3);
            if (prevWords.length === 0 || currentWords.length === 0) return false;
            const commonWords = prevWords.filter(w => currentWords.includes(w));
            return commonWords.length / Math.max(prevWords.length, currentWords.length) > 0.5;
          })
      : false;
    
    // For "explain further", we already validated above - if we reach here, it passed validation
    // For other messages, check general validation
    if (!isExplainFurther) {
    if (formatted && formatted.trim() && !isGeneric && !isQuestion && !isRepetitive) {
      return formatted;
    }
    // If response is empty, generic, or asking questions, use context-aware fallback
    throw new Error('Response too generic, empty, or asking questions');
    }
    
    // For "explain further", if we reach here, validation passed - return the formatted response
    return formatted;
  } catch (error: any) {
    logger.error('OpenAI failed or returned generic response:', error.message);
    
    // For "explain further", ALWAYS use fallback which extracts the rule
    const userMessage = (context.userMessage || '').toLowerCase().trim();
    const isExplainFurther = userMessage.includes('explain further') || 
                             userMessage.includes('explain more') ||
                             (context.language === 'nl' && (userMessage.includes('leg uit') || userMessage.includes('meer uitleg') || userMessage.includes('leg verder'))) ||
                             (context.language === 'ar' && (userMessage.includes('اشرح أكثر') || userMessage.includes('مزيد من الشرح')));
    
    if (isExplainFurther) {
      // For "explain further", always use fallback which extracts the key rule
      logger.info('Using fallback for "explain further" - extracting rule from explanation');
      return getFallbackResponse(context);
    }
    
    // Try context-aware fallback first for other messages
    if (userMessage.trim()) {
      const contextAwareFallback = getFallbackResponse(context);
      if (contextAwareFallback && !contextAwareFallback.toLowerCase().includes('great progress')) {
        return contextAwareFallback;
      }
    }
    
    // Use mistake-type specific responses
    if (context.mistakeType === 'careless') {
      return language === 'nl'
        ? 'Let op het detail in de regel en pas die hier toe.'
        : language === 'ar'
        ? 'انتبه للتفصيل في القاعدة وطبّقه هنا.'
        : 'Notice the key detail in the rule and apply it here.';
    }
    if (context.mistakeType === 'correct') {
      return language === 'nl'
        ? 'Goed gezien. Dit is precies de regel die hier geldt.'
        : language === 'ar'
        ? 'جيد. هذه هي القاعدة التي تنطبق هنا.'
        : 'Good. This is the rule that applies here.';
    }
    
    // Final fallback - context-aware
    return getFallbackResponse(context);
  }
};

/**
 * Generate check-in message (when struggling) - SHORT and CONCISE
 */
export const generateCheckIn = async (
  openai: any,
  useOpenAI: boolean,
  focusArea: any | null,
  recentMistakes: number,
  mistakesInTopic: number,
  currentTopic: string,
  language: string
): Promise<string> => {
  // Use OpenAI for better quality if available
  if (useOpenAI && openai) {
    try {
      const prompt = buildCheckInPrompt(
        focusArea,
        recentMistakes,
        mistakesInTopic,
        currentTopic,
        language
      );
      const response = await Promise.race([
        callOpenAI(openai, prompt, language),
        new Promise<string>((_, reject) => 
          setTimeout(() => reject(new Error('OpenAI timeout')), 5000)
        )
      ]);
      return formatResponse(response, language);
    } catch (error: any) {
      logger.debug('OpenAI check-in failed, using template:', error.message);
      // Fall through to template
    }
  }

  // Fallback to short templates
  if (focusArea) {
    const areaName = focusArea.testName || focusArea.testId || 'this area';
    const templates: Record<string, string[]> = {
      en: [
        `I notice ${areaName} are challenging. Slow down and read carefully.`,
        `${areaName} are tricky. Take your time.`,
        `${areaName} need attention. Read carefully.`
      ],
      nl: [
        `Ik merk dat ${areaName} lastig zijn. Neem je tijd en lees goed.`,
        `${areaName} zijn lastig. Neem je tijd.`,
        `${areaName} hebben aandacht nodig. Lees goed.`
      ],
      ar: [
        `ألاحظ أن ${areaName} صعبة. خذ وقتك واقرأ بعناية.`,
        `${areaName} صعبة. خذ وقتك.`,
        `${areaName} تحتاج إلى انتباه. اقرأ بعناية.`
      ]
    };
    const langTemplates = templates[language] || templates.en;
    return langTemplates[Math.floor(Math.random() * langTemplates.length)];
  } else {
    const templates: Record<string, string[]> = {
      en: [
        "Take your time and read carefully.",
        "Slow down and focus.",
        "Read each question carefully."
      ],
      nl: [
        "Neem je tijd en lees goed.",
        "Ga langzamer en focus.",
        "Lees elke vraag goed."
      ],
      ar: [
        "خذ وقتك واقرأ بعناية.",
        "ابطئ وتركّز.",
        "اقرأ كل سؤال بعناية."
      ]
    };
    const langTemplates = templates[language] || templates.en;
    return langTemplates[Math.floor(Math.random() * langTemplates.length)];
  }
};
